// This file exports types that match the Prisma schema
export const Role = {
  ADMIN: "ADMIN",
  CLIENT: "CLIENT",
  ADMIN_READ_ONLY: "ADMIN_READ_ONLY",
} as const;

export const OrderStatus = {
  PENDING: "PENDING",
  PAID: "PAID",
  PROCESSING: "PROCESSING",
  SHIPPED: "SHIPPED",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
} as const;

export const PaymentStatus = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  REFUNDED: "REFUNDED",
} as const;

export const PaymentMethod = {
  NETOPIA: "NETOPIA",
  RAMBURS: "RAMBURS",
} as const;

export const ShippingStatus = {
  REGISTERED: "REGISTERED",
  IN_TRANSIT: "IN_TRANSIT",
  DELIVERED: "DELIVERED",
  CANCELED: "CANCELED",
  FAILED: "FAILED",
  RETURNED: "RETURNED",
  PENDING: "PENDING",
} as const;

export const InvoiceStatus = {
  ISSUED: "ISSUED",
  CANCELED: "CANCELED",
  STORNO: "STORNO",
} as const;

export const VerificationStatus = {
  PENDING: "PENDING",
  VERIFIED: "VERIFIED",
  EXPIRED: "EXPIRED",
  FAILED: "FAILED",
} as const;

export const TrackingSessionStatus = {
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

export const ProductStatus = {
  DRAFT: "DRAFT",
  ACTIVE: "ACTIVE",
  ARCHIVED: "ARCHIVED",
} as const;

export const InventoryPolicy = {
  DENY: "DENY",
  CONTINUE: "CONTINUE",
} as const;

export const WeightUnit = {
  KG: "KG",
  G: "G",
  LB: "LB",
  OZ: "OZ",
} as const;

export const MetafieldType = {
  STRING: "STRING",
  INTEGER: "INTEGER",
  BOOLEAN: "BOOLEAN",
  DATE: "DATE",
  JSON: "JSON",
} as const;

export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];
export type ShippingStatus =
  (typeof ShippingStatus)[keyof typeof ShippingStatus];
export type Role = (typeof Role)[keyof typeof Role];
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];
export type VerificationStatus =
  (typeof VerificationStatus)[keyof typeof VerificationStatus];
export type TrackingSessionStatus =
  (typeof TrackingSessionStatus)[keyof typeof TrackingSessionStatus];
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus];
export type InventoryPolicy =
  (typeof InventoryPolicy)[keyof typeof InventoryPolicy];
export type WeightUnit = (typeof WeightUnit)[keyof typeof WeightUnit];
export type MetafieldType = (typeof MetafieldType)[keyof typeof MetafieldType];

export interface User {
  id: number;
  email: string;
  password: string;
  name?: string | null;
  role: Role;
  lastLoginAt?: string | null;
  refreshToken?: string | null;
  twoFactorSecret?: string | null;
  twoFactorEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastIp?: string | null;
  orders?: Order[];
  userDetails?: UserDetails | null;
}

export interface UserDetails {
  id: number;
  userId: number;
  phone?: string | null;
  fullAddress?: string | null;
  judet?: string | null;
  localitate?: string | null;
  strada?: string | null;
  numar?: string | null;
  bloc?: string | null;
  scara?: string | null;
  apartament?: string | null;
  user: User;
}

export interface ProductType {
  id: number;
  name: string;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  products?: Product[];
}

export interface ProductOption {
  id: number;
  productId: number;
  name: string;
  position: number;
  createdAt: string;
  updatedAt: string;
  product: Product;
  values?: ProductOptionValue[];
  variantOptions?: ProductVariantOption[];
}

export interface ProductOptionValue {
  id: number;
  optionId: number;
  value: string;
  position: number;
  createdAt: string;
  updatedAt: string;
  option: ProductOption;
  variants?: ProductVariantOption[];
}

export interface ProductVariantOption {
  id: number;
  variantId: number;
  optionId: number;
  valueId: number;
  variant: ProductVariant;
  option: ProductOption;
  value: ProductOptionValue;
}

export interface ProductImage {
  id: number;
  productId: number;
  url: string;
  altText?: string | null;
  position: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  product: Product;
}

export interface ProductMetafield {
  id: number;
  productId: number;
  namespace: string;
  key: string;
  value: string;
  type: MetafieldType;
  createdAt: string;
  updatedAt: string;
  product: Product;
}

export interface ProductCategory {
  id: number;
  productId: number;
  categoryId: number;
  createdAt: string;
  product: Product;
  category: Category;
}

export interface Subcategory {
  id: number;
  categoryId: number;
  name: string;
  slug: string;
  description?: string | null;
  displayOrder?: number | null;
  createdAt: string;
  updatedAt: string;
  category: Category;
  products?: ProductSubcategory[];
}

export interface ProductSubcategory {
  id: number;
  productId: number;
  subcategoryId: number;
  createdAt: string;
  product: Product;
  subcategory: Subcategory;
}
export interface Review {
  id: number;
  productId: number;
  productVariantId?: number | null;
  name: string;
  rating: number;
  title?: string | null;
  text: string;
  createdAt: string;
  updatedAt: string;
  product: Product;
  variant?: ProductVariant | null;
}

export interface ProductVariant {
  id: number;
  productId: number;
  title: string;
  sku?: string | null;
  barcode?: string | null;
  price: number;
  compareAtPrice?: number | null;
  costPrice?: number | null;
  inventoryQuantity: number;
  inventoryPolicy: InventoryPolicy;
  inventoryTracked: boolean;
  requiresShipping: boolean;
  weight?: number | null;
  weightUnit?: WeightUnit | null;
  createdAt: string;
  updatedAt: string;
  product?: Product;
  optionValues?: ProductVariantOption[];
  orderItems?: OrderItem[];
  reviews?: Review[];
  stockSyncItems?: StockSyncItem[];
}

export interface PriceInfo {
  price: number | null;
  priceRange: string | null;
  hasMultiplePrices: boolean;
  minPrice: number | null;
  maxPrice: number | null;
}

export interface Product {
  id: number;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  status: ProductStatus;
  vendor?: string | null;
  handle?: string | null;
  productTypeId?: number | null;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  options?: ProductOption[];
  variants?: ProductVariant[];
  images?: ProductImage[];
  metafields?: ProductMetafield[];
  reviews?: Review[];
  orderItems?: OrderItem[];
  stockSyncItems?: StockSyncItem[];
  productType?: ProductType | null;
  priceInfo?: PriceInfo | null;

  // Promo
  freeShippingEnabled?: boolean;
  freeShippingMinQty?: number | null;

  // Legacy aliases used by some components
  name?: string;
  price?: number;
  stock?: number;
  hide?: boolean;
  imageUrl?: string;
  imageUrlOnPage?: string;
  cod?: number;
  awbCode?: string;
  categories?: { category: Category }[];
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  imageUrl?: string | null;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  displayOrder: number;
  products?: ProductCategory[];
  subcategories?: ProductSubcategory[];
}

export interface Payment {
  id: number;
  orderId: number;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  netopiaPaymentId?: string | null;
  netopiaPaymentUrl?: string | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
  order: Order;
}

export interface SmsVerification {
  id: number;
  orderId: number;
  phoneNumber: string;
  code: string;
  attempts: number;
  status: VerificationStatus;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  order: Order;
}

interface OrderMetadata {
  awbPrinted?: boolean;
  lockerId?: number;
  lockerName?: string;
  awbPrintedAt?: string;
  observatii?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  billingAddress?: string;
  company?: {
    cui?: string | null;
    isVatPayer?: boolean;
  };
  paymentStatus?: "PAID" | "UNPAID" | "PARTIALLY_PAID" | "PENDING";
  manuallyVerified?: boolean;
  phoneVerified?: boolean;
  phoneVerificationMethod?: "SMS" | "MANUAL";
  phoneVerificationDate?: string;
  confirmationEmailSent?: boolean;
  shippingEmailSent?: boolean;
  shippingEmailSentAt?: string;
  customerAddress?: string;
  deliveryPrice?: number;
  codFee?: number;
  promo?: {
    totalUnits: number;
    discountUnits: number;
    discountAmount: number;
    variantIdsInOrder: number[];
  };
  lastTrackingUpdate?: string;
  shippingStatusDetails?: {
    statusId: number;
    statusState: string;
    statusLabel: string;
    timestamp: string;
    transitLocation?: string;
    reason?: string;
    deliveryAttempts?: number;
    deliveryProgress: number;
  };
  trackingHistory?: Array<{
    status: string;
    samedayStatus: number;
    samedayLabel: string;
    samedayState: string;
    timestamp: string;
    transitLocation?: string;
    reason?: string;
    deliveryProgress: number;
  }>;
  invoice?: {
    status: string;
    issuedAt: string;
    documentId: number;
    paidAmount: number;
    unpaidAmount: number;
    paymentStatus: string;
    documentViewUrl: string;
  };
  shipping?: {
    status: string;
    timestamp: string;
  };
  items?: Array<{
    price: number;
    bookId: string;
    quantity: number;
  }>;
}

export interface OrderItem {
  id: number;
  orderId: number;
  productId?: number | null;
  productVariantId?: number | null;
  productTitle: string;
  variantTitle?: string | null;
  sku?: string | null;
  quantity: number;
  price: number;
  totalPrice: number;
  createdAt: string;
  updatedAt: string;
  order?: Order;
  product?: Product | null;
  variant?: ProductVariant | null;
}

export interface Order {
  id: number;
  userId: number;
  totalPrice: number;
  status: OrderStatus;
  fullAddress: string;
  judet: string;
  localitate: string;
  strada: string;
  numar: string;
  bloc?: string | null;
  scara?: string | null;
  apartament?: string | null;
  awbNumber?: string | null;
  awbUrl?: string | null;
  samedayCountyId?: number | null;
  samedayCityId?: number | null;
  shippingStatus?: ShippingStatus | null;
  shippingStatusDetails?: any;
  invoiceNumber?: string | null;
  invoiceSeries?: string | null;
  invoiceUrl?: string | null;
  invoiceStatus?: InvoiceStatus | null;
  metadata?: OrderMetadata;
  createdAt: string;
  updatedAt: string;
  clientIp?: string | null;
  user: User;
  orderItems: OrderItem[];
  verification?: SmsVerification | null;
  payment?: Payment | null;
}

export interface Blacklist {
  id: number;
  phone: string;
  failureCount: number;
  isBlacklisted: boolean;
  reason?: string | null;
  lastStatusId?: number | null;
  lastOrderId?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface IpBlacklist {
  id: number;
  ip: string;
  isBlacklisted: boolean;
  failureCount: number;
  reason?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ErrorLog {
  id: number;
  level: string;
  message: string;
  stack?: string | null;
  details?: any;
  timestamp: string;
}

export interface ExternalApiToken {
  id: number;
  provider: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SamedayCounty {
  id: number;
  name: string;
  code?: string | null;
  updatedAt: string;
  cities?: SamedayCity[];
  lockers?: SamedayLocker[];
}

export interface SamedayCity {
  id: number;
  name: string;
  countyId: number;
  postalCode?: string | null;
  extraKm?: number | null;
  updatedAt: string;
  county: SamedayCounty;
  lockers?: SamedayLocker[];
}

export interface SamedayLocker {
  id: number;
  name: string;
  country?: string | null;
  countryId?: number | null;
  countyId?: number | null;
  cityId?: number | null;
  address?: string | null;
  postalCode?: string | null;
  lat?: number | null;
  lng?: number | null;
  phone?: string | null;
  email?: string | null;
  clientVisible?: number | null;
  supportedPayment?: number | null;
  deliveryLogisticLocationId?: number | null;
  deliveryLogisticLocation?: string | null;
  occupancyLevel?: number | null;
  lockerRoute?: string | null;
  schedule?: any;
  availableBoxes?: any;
  reservedBoxes?: any;
  occupiedBoxes?: any;
  updatedAt: string;
  city?: SamedayCity | null;
  county?: SamedayCounty | null;
}

export interface NomenclatorSync {
  id: number;
  lastSync: string;
  status: string;
  error?: string | null;
  progress?: any;
  updatedAt: string;
}

export interface AdminLoginAttempt {
  id: number;
  ip: string;
  attempts: number;
  blockedUntil?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Draft {
  id: number;
  email: string;
  name: string;
  phone: string;
  createdAt: string;
  updatedAt: string;
}

export interface TrackingSession {
  id: number;
  status: TrackingSessionStatus;
  startedAt: string;
  completedAt?: string | null;
  totalAwbsChecked: number;
  totalStatusesChanged: number;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StockSync {
  id: number;
  syncedAt: string;
  targetDate: string;
  totalUpdated: number;
  items?: StockSyncItem[];
}

export interface StockSyncItem {
  id: number;
  stockSyncId: number;
  productId?: number | null;
  productVariantId?: number | null;
  sku?: string | null;
  productTitle: string;
  variantTitle?: string | null;
  previousQuantity: number;
  newQuantity: number;
  diff: number;
  stockSync: StockSync;
  product?: Product | null;
  variant?: ProductVariant | null;
}

export interface UserDeliveredOrdersCache {
  id: number;
  userId: number;
  count: number;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  User: User;
}

export interface FeatureFlag {
  id: number;
  key: string;
  enabled: boolean;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}
